import React, { useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardText,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Progress,
  Spinner
} from "reactstrap";
import { bulkInsert, bulkUpdate } from "../services/bulkServices";
import { useMsal } from "@azure/msal-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import "./SeedCollection.css";

const CALL_DELAY = 1500;
const INSERT_MODE = "upsert";
const delay = (ms) => new Promise((res) => setTimeout(res, ms));
const Json = ({ data }) => <pre>{JSON.stringify(data, null, 4)}</pre>;

const COLLECTIONS = [
  { name: "Users", label: "Users" },
  { name: "Tasks", label: "Tasks" }
];

const SeedCollection = () => {
  const { instance, accounts } = useMsal();

  const [isLoading, setIsLoading] = useState(false);
  const [collection, setCollection] = useState("");
  const [dataDoc, setDataDoc] = useState("[{}]");

  const [transferProgress, setTransferProgress] = useState(0);
  const [transferTotalRecords, setTransferTotalRecords] = useState(0);
  const [isTransferring, setIsTransferring] = useState(false);

  const [insertError, setInsertError] = useState(null);

  const getIdToken = async () => {
    const res = await instance.acquireTokenSilent({
      scopes: ["User.Read", "User.ReadBasic.All"],
      account: accounts[0]
    });

    return res.idToken;
  };

  const handleCollection = (coll) => {
    setCollection(coll);
  };

  const handleDataDoc = (doc) => {
    setDataDoc(doc);
  };

  const isValidJson = (str) => {
    let temp = null;

    try {
      temp = JSON.parse(str);

      return Array.isArray(temp) && temp.length;
    } catch (e) {
      return false;
    }
  };

  const validateForm = () => {
    const isCollection = collection !== "";
    const isData = dataDoc !== "";
    const isDataValid = isValidJson(dataDoc);

    return isCollection && isData && isDataValid;
  };

  const TransferProgressBar = () => {
    const isPlural = transferTotalRecords > 1;

    return (
      <Card body>
        <CardText className="text-center">
          {transferProgress} of {transferTotalRecords} record
          {isPlural ? "s" : ""} inserted
        </CardText>
        <Progress
          color="success"
          value={transferProgress}
          max={transferTotalRecords}
        />
      </Card>
    );
  };

  const ShowErrors = () => {
    return (
      <Card>
        <CardHeader className="card-header-danger">
          <div>
            Encountered error
            <Button
              color="danger"
              style={{ float: "right" }}
              size="sm"
              onClick={() => setInsertError(null)}
              className="collapse-button"
            >
              <FontAwesomeIcon icon={faTimesCircle} />
            </Button>
          </div>
        </CardHeader>
        <CardBody className="overflow-500">
          <Json data={insertError} />
        </CardBody>
      </Card>
    );
  };

  const handleInsert = async () => {
    setIsLoading(true);
    setInsertError(null);

    // Request access token
    const idToken = await getIdToken();

    const docs = JSON.parse(dataDoc);

    setTransferTotalRecords(docs.length);
    setIsTransferring(true);

    for (let i = 0; i < docs.length; i++) {
      setTransferProgress(i + 1);

      try {
        let res = null;
        const doc = docs[i];

        if (INSERT_MODE === "insert") {
          const payload = {
            collection,
            docs: [doc],
            confirm: true
          };

          res = await bulkInsert(payload, idToken);
        }

        if (INSERT_MODE === "upsert") {
          const payload = {
            collection,
            filter: {
              id: doc.id
            },
            update: {
              $set: doc,
              $push: {},
              $pull: {},
              $setOnInsert: {}
            },
            options: {
              upsert: true
            },
            confirm: true
          };

          res = await bulkUpdate(payload, idToken);
        }

        if (res.status !== 200) {
          setIsTransferring(false);
          setIsLoading(false);
          setInsertError({ item: i + 1, error: res.response });

          return false;
        }
      } catch (e) {
        setIsTransferring(false);
        setIsLoading(false);
        setInsertError({ item: i + 1, error: e.message });

        return false;
      }

      await delay(CALL_DELAY);
    }

    setIsTransferring(false);
    setIsLoading(false);
  };

  return (
    <div className="SeedCollection">
      <Card>
        <CardHeader tag="h5">Seed Collections</CardHeader>
        <CardBody>
          <Card>
            <CardHeader>Bulk Insert</CardHeader>
            <CardBody>
              <Form>
                <FormGroup>
                  <Label for="collection-select">Collection</Label>
                  <Input
                    type="select"
                    name="collection-select"
                    id="collection-select"
                    onChange={(e) => handleCollection(e.target.value)}
                    disabled={isLoading}
                  >
                    <option value="">Select collection from list</option>
                    {COLLECTIONS.map((coll, i) => {
                      return (
                        <option key={i} value={coll.name}>
                          {coll.label}
                        </option>
                      );
                    })}
                  </Input>
                </FormGroup>
                <FormGroup>
                  <Label for="batch-data">Data (JSON)</Label>
                  <Input
                    invalid={!isValidJson(dataDoc)}
                    type="textarea"
                    name="batch-data"
                    id="batch-data"
                    value={dataDoc}
                    onChange={(e) => handleDataDoc(e.target.value)}
                    disabled={isLoading}
                  />
                  <FormFeedback>
                    Invalid JSON array or array is empty
                  </FormFeedback>
                </FormGroup>
              </Form>
              {isTransferring && <TransferProgressBar />}
              {insertError && <ShowErrors />}
            </CardBody>
            <CardFooter>
              <Button
                outline
                color="primary"
                size="sm"
                onClick={() => handleInsert()}
                disabled={isLoading || !validateForm()}
              >
                Process batch {isLoading && <Spinner size="sm" color="dark" />}
              </Button>
            </CardFooter>
          </Card>
        </CardBody>
      </Card>
    </div>
  );
};

export default SeedCollection;
